import {StyleSheet} from 'react-native';

import theme from '../../style/theme';

/**
 * Unified styling constants for ProductTile components.
 * Single source of truth for all dimensions, typography, colors, and interactions.
 * No magic numbers should exist in primitive or variant files.
 */

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const TYPOGRAPHY = {
  name: {fontSize: 16, fontWeight: 'bold' as const},
  nameHorizontal: {fontSize: 14, fontWeight: 'bold' as const},
  price: {fontSize: 20, fontWeight: 'bold' as const},
  priceHorizontal: {fontSize: 16, fontWeight: 'bold' as const},
  priceUnit: {fontSize: 12 as const},
  byline: {fontSize: 11 as const},
  wasPrice: {fontSize: 12 as const},
  buttonLabel: {fontSize: 14, fontWeight: 'bold' as const},
  shopInstore: {fontSize: 12, fontWeight: 'bold' as const},
  quantity: {fontSize: 16, fontWeight: 'bold' as const},
  memberLabel: {fontSize: 14, fontWeight: 'bold' as const},
  previousQuantity: {fontSize: 12, fontStyle: 'italic' as const},
} as const;

// =============================================================================
// DIMENSIONS
// =============================================================================

export const TILE_DIMENSIONS = {
  /** Fixed width for vertical tiles (carousel/compact) */
  width: 150,
  /** Padding inside tile container (matches production) */
  containerPadding: 6,
  /** Border radius for tiles */
  borderRadius: 4,
  /** Border width for tiles */
  borderWidth: 0.5,
  /** Height of members price label */
  memberLabelHeight: 22,
  /** Horizontal padding inside members label */
  memberLabelPaddingH: 4,
  /** Border width for private members label */
  memberLabelBorderWidth: 2,
  /** Top margin below members label */
  imageMarginTop: 4,
  /** Bottom margin below image */
  imageMarginBottom: 8,
  /** Bottom margin below info section */
  infoMarginBottom: 4,
  /** Bottom margin below pricing section */
  pricingMarginBottom: 8,
  /** Minimum height for pricing container (vertical tiles) */
  pricingMinHeight: 70,
  /** Top margin for byline text */
  bylineMarginTop: 2,
  /** Left margin for price unit text */
  priceUnitMarginLeft: 4,
  /** Top margin for secondary callout label */
  secondaryCalloutMarginTop: 6,
  /** Minimum height for horizontal tile */
  horizontalMinHeight: 96,
  /** Image container width in horizontal tile */
  horizontalImageWidth: 80,
  /** Content left padding in horizontal tile */
  horizontalContentPaddingLeft: 10,
  /** Vertical margin for previous quantity row */
  horizontalPreviousQtyMarginV: 4,
  /** Compact MembersPriceLabel height for horizontal tiles */
  horizontalMemberLabelHeight: 18,
  /** Compact MembersPriceLabel horizontal padding */
  horizontalMemberLabelPaddingH: 6,
  /** Gap between MembersPriceLabel and product name */
  horizontalMemberLabelGap: 6,
  /** Gap between price segments in dual pricing */
  horizontalPriceSegmentGap: 8,
  /** Shadow offset height (matches production) */
  shadowOffsetHeight: 5,
  /** Shadow opacity (matches production) */
  shadowOpacity: 0.29,
  /** Shadow radius (matches production) */
  shadowRadius: 4.65,
  /** Android elevation (matches production) */
  elevation: 7,
  /** Horizontal margin for carousel scroll context */
  carouselMarginH: 0,
  /** Fixed height for product name (2 lines at 16px) */
  nameFixedHeight: 40,
} as const;

export const IMAGE_SIZES = {
  small: {width: 72, height: 72},
  medium: {width: 140, height: 140},
  large: {width: 200, height: 200},
} as const;

// =============================================================================
// INTERACTION
// =============================================================================

export const INTERACTION = {
  pressedOpacity: 0.7,
  quantityPressedOpacity: 0.8,
  loadingTextOpacity: 0.5,
} as const;

// =============================================================================
// COLOR CONSTANTS (all theme-referenced)
// =============================================================================

export const COLORS = {
  /** Primary button background (brand color) */
  buttonPrimary: theme.colour.background.button.primary,
  /** Button icon/text color */
  buttonIcon: theme.colour.text.tile.button,
  /** Tile border color */
  tileBorder: theme.colour.border.tile,
  /** Product name text color */
  nameText: theme.colour.text.tile.name,
  /** Byline text color */
  bylineText: theme.colour.text.tile.byline,
  /** Price text color */
  priceText: theme.colour.text.tile.price,
  /** Secondary text color (was, original price) */
  secondaryText: theme.colour.text.tile.priceSecondary,
} as const;

// =============================================================================
// CART BUTTON STYLING
// =============================================================================

export const CART_BUTTON_DIMENSIONS = {
  /** Minimum height for cart buttons */
  minHeight: 30,
  /** Border radius for cart buttons */
  borderRadius: 2,
  /** Icon size inside cart buttons (smaller for better visual balance) */
  iconSize: 16,
  /** Padding around icons in quantity buttons */
  iconPadding: 4,
} as const;

/**
 * Unified cart button styles for ProductTile context.
 * Quantity selector design: Outline style with brand-colored borders
 * - Normal: White background, brand-colored border/icons
 * - Pressed: Brand-colored background, white icons (inverted)
 */
export const cartButtonStyles = StyleSheet.create({
  /** Container for quantity selector (-, count, +) */
  quantityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: CART_BUTTON_DIMENSIONS.minHeight,
    borderRadius: CART_BUTTON_DIMENSIONS.borderRadius,
    overflow: 'hidden',
    width: '100%',
    borderWidth: 1,
    borderColor: theme.colour.background.button.primary,
  },

  /** Individual +/- button - square shape, solid brand style */
  quantityButton: {
    width: CART_BUTTON_DIMENSIONS.minHeight,
    minHeight: CART_BUTTON_DIMENSIONS.minHeight,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colour.background.button.primary,
  },

  /** Individual +/- button - pressed/active state */
  quantityButtonPressed: {
    opacity: INTERACTION.quantityPressedOpacity,
  },

  /** Count display between +/- buttons */
  quantityDisplay: {
    flex: 1,
    minHeight: CART_BUTTON_DIMENSIONS.minHeight,
    paddingHorizontal: TILE_DIMENSIONS.containerPadding,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colour.background.tile.surface,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: theme.colour.background.button.primary,
  },

  /** Text showing quantity count */
  quantityText: {
    ...TYPOGRAPHY.quantity,
    color: theme.colour.background.button.primary,
  },

  /** ADD TO CART button - solid style with border to match quantity selector height */
  addToCartButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: CART_BUTTON_DIMENSIONS.minHeight,
    backgroundColor: theme.colour.background.button.primary,
    borderRadius: CART_BUTTON_DIMENSIONS.borderRadius,
    paddingHorizontal: TILE_DIMENSIONS.containerPadding,
    paddingVertical: 4,
    width: '100%',
    borderWidth: 1,
    borderColor: theme.colour.background.button.primary,
  },

  /** ADD TO CART button text */
  addToCartText: {
    ...TYPOGRAPHY.buttonLabel,
    color: theme.colour.text.tile.button,
  },

  /** Loading overlay positioned over ADD TO CART button */
  addToCartLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },

  /** Dimmed text when loading */
  addToCartTextLoading: {
    opacity: INTERACTION.loadingTextOpacity,
  },

  /** Disabled/SHOP INSTORE button */
  disabledButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colour.background.tile.buttonDisabled,
    borderRadius: CART_BUTTON_DIMENSIONS.borderRadius,
    borderColor: theme.colour.border.general.primary,
    borderWidth: 1,
    paddingHorizontal: TILE_DIMENSIONS.containerPadding,
    paddingVertical: 4,
    width: '100%',
    minHeight: CART_BUTTON_DIMENSIONS.minHeight,
  },

  /** SHOP INSTORE text */
  shopInstoreText: {
    ...TYPOGRAPHY.shopInstore,
    color: theme.colour.text.heading.primary,
  },
});

// =============================================================================
// IMAGE STYLING
// =============================================================================

export const imageStyles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    aspectRatio: 1,
  },
});

// =============================================================================
// TILE CONTAINER STYLING
// =============================================================================

export const tileContainerStyles = StyleSheet.create({
  /** Base container style for all tiles */
  container: {
    backgroundColor: theme.colour.background.tile.surface,
    borderRadius: TILE_DIMENSIONS.borderRadius,
    borderWidth: TILE_DIMENSIONS.borderWidth,
    borderColor: theme.colour.border.tile,
    overflow: 'hidden',
  },

  /** Pressed state opacity */
  pressed: {
    opacity: INTERACTION.pressedOpacity,
  },

  /** Shadow for carousel/elevated tiles */
  shadow: {
    shadowColor: '#000',
    shadowOffset: {width: 0, height: TILE_DIMENSIONS.shadowOffsetHeight},
    shadowOpacity: TILE_DIMENSIONS.shadowOpacity,
    shadowRadius: TILE_DIMENSIONS.shadowRadius,
    elevation: TILE_DIMENSIONS.elevation,
  },
});

// =============================================================================
// INFO STYLING
// =============================================================================

export const infoStyles = StyleSheet.create({
  container: {},
  name: {
    ...TYPOGRAPHY.name,
    color: theme.colour.text.tile.name,
  },
  byline: {
    ...TYPOGRAPHY.byline,
    color: theme.colour.text.tile.byline,
    marginTop: TILE_DIMENSIONS.bylineMarginTop,
  },
});

// =============================================================================
// ACTIONS CONTAINER STYLING
// =============================================================================

export const actionsContainerStyles = StyleSheet.create({
  /** Container for action buttons */
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },

  /** Full width actions (for vertical tiles) */
  fullWidth: {
    width: '100%',
    justifyContent: 'center',
  },

  /** Wrapper for cart button in full width mode */
  cartButtonWrapper: {
    flex: 1,
  },
});

// =============================================================================
// PRICING STYLING
// =============================================================================

export const pricingStyles = StyleSheet.create({
  container: {},
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  price: {
    ...TYPOGRAPHY.price,
    color: COLORS.priceText,
    flexShrink: 0,
  },
  priceUnit: {
    ...TYPOGRAPHY.priceUnit,
    color: COLORS.priceText,
    flexShrink: 1,
    marginLeft: TILE_DIMENSIONS.priceUnitMarginLeft,
  },
  originalPriceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  wasText: {
    ...TYPOGRAPHY.wasPrice,
    color: COLORS.secondaryText,
  },
  originalPrice: {
    ...TYPOGRAPHY.wasPrice,
    color: COLORS.secondaryText,
    textDecorationLine: 'line-through',
  },
});

// =============================================================================
// HORIZONTAL PRICING OVERRIDES
// =============================================================================

export const horizontalPricingOverrides = StyleSheet.create({
  container: {
    minHeight: 0,
    justifyContent: 'flex-start',
  },
  price: {
    ...TYPOGRAPHY.priceHorizontal,
    color: theme.colour.text.tile.price,
    flexShrink: 0,
  },
});
